import { PriceEvolutionByCategory } from '../PriceEvolutionChart/PriceEvolutionChart.types'

export const formatPricePerSqm = (
  price: number,
  preserveDecimals: boolean = false
): string => {
  if (preserveDecimals) {
    // Use 2 decimals for rental m² prices (typically CHF 15-35)
    return `CHF ${price.toLocaleString('en-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/m²`
  }
  return `CHF ${Math.round(price).toLocaleString()}/m²`
}

export const formatQuarter = (dateString: string): string => {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = date.getMonth()
  const quarter = Math.floor(month / 3) + 1
  return `${year} Q${quarter}`
}

export const formatPropertyCategory = (category: string): string => {
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
}

export const calculatePriceM2KPIs = (
  data: PriceEvolutionByCategory,
  priceType: 'median' | 'average' = 'median'
) => {
  const allPrices: number[] = []
  const latestPrices: Record<string, number> = {}
  const firstPrices: Record<string, number> = {}
  let totalVolume = 0

  Object.entries(data).forEach(([category, values]) => {
    if (values.length > 0 && category !== 'All Types') {
      const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
      latestPrices[category] = values[values.length - 1][priceField]
      firstPrices[category] = values[0][priceField]
      values.forEach((v) => {
        allPrices.push(v[priceField])
        totalVolume += v.count
      })
    }
  })

  // Use "All Types" for average price if available
  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const avgPricePerSqm =
    data['All Types'] && data['All Types'].length > 0
      ? data['All Types'][data['All Types'].length - 1][priceField]
      : allPrices.length > 0
        ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length
        : 0

  const mostExpensiveCategory = Object.entries(latestPrices)
    .filter(([category]) => category !== 'All Types')
    .sort((a, b) => b[1] - a[1])[0]

  // Calculate 1-year growth using "All Types" if available
  let overallGrowth = 0
  if (data['All Types'] && data['All Types'].length > 0) {
    const allTypesData = data['All Types']
    const latest = allTypesData[allTypesData.length - 1]

    // Find data point from approximately 1 year ago (4 quarters back)
    // Since data is quarterly, we look for 4 quarters back
    const oneYearAgoIndex = Math.max(0, allTypesData.length - 5) // -5 because -1 is current, -5 is 4 quarters back
    const oneYearAgoData = allTypesData[oneYearAgoIndex]

    if (oneYearAgoData && oneYearAgoData[priceField] > 0) {
      const oneYearAgoPrice = oneYearAgoData[priceField]
      const latestPrice = latest[priceField]
      overallGrowth = ((latestPrice - oneYearAgoPrice) / oneYearAgoPrice) * 100
    }
  } else {
    // Calculate average 1-year growth across categories
    const growthRates: number[] = []
    Object.entries(data).forEach(([category, values]) => {
      if (category !== 'All Types' && values.length >= 5) {
        // Need at least 5 quarters for 1-year comparison
        const latest = values[values.length - 1]
        const oneYearAgoIndex = Math.max(0, values.length - 5)
        const oneYearAgo = values[oneYearAgoIndex]

        if (oneYearAgo && oneYearAgo[priceField] > 0) {
          const growth =
            ((latest[priceField] - oneYearAgo[priceField]) /
              oneYearAgo[priceField]) *
            100
          growthRates.push(growth)
        }
      }
    })

    overallGrowth =
      growthRates.length > 0
        ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
        : 0
  }

  return {
    avgPricePerSqm,
    overallGrowth,
    mostExpensiveCategory,
    totalVolume,
  }
}

export interface PriceM2Insight {
  title: string
  description: string
}

export const generatePriceM2Insights = (
  data: PriceEvolutionByCategory,
  priceType: 'median' | 'average' = 'median'
): PriceM2Insight[] => {
  const insights: PriceM2Insight[] = []

  if (Object.keys(data).length === 0) return insights

  // Get all categories data
  const categories = Object.keys(data)
  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

  // 1. Price/m² trend analysis
  const allTypesData = data['All Types']
  if (allTypesData && allTypesData.length >= 2) {
    const firstQuarter = allTypesData[0]
    const lastQuarter = allTypesData[allTypesData.length - 1]
    const overallChange =
      ((lastQuarter[priceField] - firstQuarter[priceField]) /
        firstQuarter[priceField]) *
      100

    insights.push({
      title: 'Price/m² Trend',
      description: `The average price per square meter has ${overallChange >= 0 ? 'increased' : 'decreased'} by ${Math.abs(overallChange).toFixed(1)}% from ${formatQuarter(firstQuarter.date)} to ${formatQuarter(lastQuarter.date)}.`,
    })
  }

  // 2. Property type comparison
  const latestPrices = categories
    .filter((cat) => cat !== 'All Types' && data[cat].length > 0)
    .map((category) => ({
      category,
      price: data[category][data[category].length - 1][priceField],
    }))
    .sort((a, b) => b.price - a.price)

  if (latestPrices.length >= 2) {
    const priceDiff =
      ((latestPrices[0].price - latestPrices[1].price) /
        latestPrices[1].price) *
      100
    insights.push({
      title: 'Property Type Premium',
      description: `${formatPropertyCategory(latestPrices[0].category)} properties command a ${priceDiff.toFixed(0)}% premium per m² compared to ${formatPropertyCategory(latestPrices[1].category)} properties.`,
    })
  }

  // 3. Market efficiency indicator
  const recentQuarters = categories
    .filter((cat) => data[cat].length >= 4)
    .map((category) => {
      const recent = data[category].slice(-4)
      const changes = []
      for (let i = 1; i < recent.length; i++) {
        const change =
          ((recent[i][priceField] - recent[i - 1][priceField]) /
            recent[i - 1][priceField]) *
          100
        changes.push(Math.abs(change))
      }
      return {
        category,
        avgChange: changes.reduce((a, b) => a + b, 0) / changes.length,
      }
    })

  if (recentQuarters.length > 0) {
    const mostStable = recentQuarters.reduce((min, curr) =>
      curr.avgChange < min.avgChange ? curr : min
    )

    insights.push({
      title: 'Market Efficiency',
      description: `${formatPropertyCategory(mostStable.category)} shows the most efficient pricing with only ${mostStable.avgChange.toFixed(1)}% average quarterly variation per m².`,
    })
  }

  // 4. Value growth leader
  const growthRates = categories
    .filter((cat) => cat !== 'All Types' && data[cat].length >= 8) // At least 2 years of data
    .map((category) => {
      const catData = data[category]
      const firstPrice = catData[0][priceField]
      const lastPrice = catData[catData.length - 1][priceField]
      const years = (catData.length - 1) / 4 // Approximate years
      const annualGrowth = Math.pow(lastPrice / firstPrice, 1 / years) - 1
      return { category, annualGrowth: annualGrowth * 100 }
    })
    .sort((a, b) => b.annualGrowth - a.annualGrowth)

  if (growthRates.length > 0) {
    const leader = growthRates[0]
    insights.push({
      title: 'Value Growth Leader',
      description: `${formatPropertyCategory(leader.category)} properties show the highest annual appreciation at ${leader.annualGrowth.toFixed(1)}% per year in price/m².`,
    })
  }

  return insights
}
